import {
  complimentaryVoucher,
  deleteVoucher,
  exportVouchers,
  getVoucherList,
} from 'app/api/voucher';
import { startExportPoll } from 'app/utils/exportPoller';
import { VOUCHER_PASSWORD } from 'app/constants/api-contants';
import { APP_CONSTANTS, LocalSessionKey } from 'app/constants/app-constants';
import Message from 'app/utils/Message';
import { LocalStorage } from 'app/utils/storage';
import Toast from 'app/utils/Toast';
import { useCallback, useEffect, useState } from 'react';
import { confirmAlert } from 'react-confirm-alert';
import { useNavigate } from 'react-router-dom';

const useVoucherManagement = () => {
  const [isExporting, setIsExporting] = useState(false);
  const [countcomferomTable, setCountcomferomTable] = useState(1);
  const [key, setkey] = useState('');
  const [tempdata, settempdata] = useState({});
  const [passwordVal, setPasswordVal] = useState("");

    const [passwordmodelOpen, setPasswordmodelOpen] = useState(false);
  const [isdeleteFiltering, setIsdeleteFiltering] = useState(false);

  // Game/Regular voucher-type filter (VOUCH-02, D-13). Tri-state:
  // 'all' | true | false. Defaults to 'all' so the list shows every voucher
  // on load; the backend's isGamificationVoucher=false safe-default only
  // applies when the param is omitted entirely, which no longer happens
  // once this hook always forwards an explicit value. This is a wholly
  // separate state trio from the `values`/`showModal` pair below, which
  // drives the unrelated Complimentary Voucher modal (RESEARCH Pitfall
  // 11) — do NOT merge these.
  const [filterValues, setFilterValues] = useState({
    isGamificationVoucher: 'all',
  });
  const [appliedFilters, setAppliedFilters] = useState({
    isGamificationVoucher: 'all',
  });
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [isFiltering, setIsFiltering] = useState(false);

  const handleExport = async () => {
    setIsExporting(true);
    try {
      const response = await exportVouchers({
        mallId: LocalStorage.get(LocalSessionKey.MALL_ID),
      });
      if (response?.responseCode !== 200) {
        Toast.error(response?.response?.message || Message.Error.COMMON_MESSAGE);
        setIsExporting(false);
        return;
      }

      const jobId = response?.data?.jobId;
      if (!jobId) {
        Toast.error(Message.Error.COMMON_MESSAGE);
        setIsExporting(false);
        return;
      }

      Toast.success('Export started. Preparing your file...');

      startExportPoll(jobId, {
        onDone: (url) => {
          setIsExporting(false);
          window.location.href = url;
          Toast.success('Export ready. File download started.');
        },
        onFailed: (error) => {
          setIsExporting(false);
          Toast.error(error || Message.Error.COMMON_MESSAGE);
        },
        onError: () => {
          setIsExporting(false);
          Toast.error(Message.Error.COMMON_MESSAGE);
        },
      });
    } catch {
      Toast.error(Message.Error.COMMON_MESSAGE);
      setIsExporting(false);
    }
  };

  // The page heading is rendered by the Voucher Management shell above the tabs,
  // so this pane passes no title.
  const sectionObj = {
    searchObj: {
      keys: ['voucherTitle', 'mallName', 'storeName'],
      placeholder: 'Search by Title, Mall name, Store Name',
      searchType: 'server',

      handleSearch: (e) => {},
    },
    buttons: [
      {
        customclassName: 'bg-white margin-right-class',
        imgTrue: false,
        title: 'Filter',
        handleBtnClick: () => setIsFilterOpen((prev) => !prev),
      },
      {
        customclassName: 'bg-white margin-right-class',
        imgTrue: false,
        title: isExporting ? 'Exporting...' : 'Export',
        handleBtnClick: handleExport,
        isDisabled: isExporting,
      },
      {
        customclassName: '',
        imgTrue: true,
        title: APP_CONSTANTS.CREATE_VOUCHER_TAG,
        handleBtnClick: (e) => {
          handlePasswordModel(e,'add')
        },
      },
    ],
  };
  const navigate = useNavigate();
  const [pageCount, setPageCount] = useState(1);
  const [pageLoading, setPageLoading] = useState(false);
  const dataPerPage = 10;
  const [filteredData, setFilteredData] = useState([]);
  const [totalNumber, setTotalNumber] = useState(0);
  const [values, setValues] = useState({
    mobileNumber: null,
    serialNumber: null,
  });
  const [showModal, setShowModal] = useState(false);
  const [error, setError] = useState('');
  const [selectedVoucher, setSelectedVoucher] = useState(null);

  const fetchVoucherData = useCallback(
    async (page, search = '', filters = appliedFilters) => {
      setPageLoading(true);
      try {
        const params = {
          page,
          limit: dataPerPage,
          search,
        };
        // filterValues/appliedFilters default to 'all' (not ''), so this
        // forwards an explicit tri-state value ('all' | true | false) on
        // every call, including the initial page load — the admin sees
        // every voucher by default. The '' / undefined / null guard stays
        // as a defensive fallback only; it should not normally trigger.
        if (
          filters?.isGamificationVoucher !== '' &&
          filters?.isGamificationVoucher !== undefined &&
          filters?.isGamificationVoucher !== null
        ) {
          params.isGamificationVoucher = filters.isGamificationVoucher;
        }
        const response = await getVoucherList(params);
        if (response?.responseCode === 200) {
          const dataArr =
            response?.data?.data?.map((item) => ({
              ...item,
              totalAvailableVouchers:
                item?.totalCount - item?.claimedVoucherCount,
            })) || [];
          setFilteredData(dataArr);
          const totalCount = response?.data?.count;
          setTotalNumber(totalCount);
          const tempcount = Math.ceil(totalCount / dataPerPage);
          setPageCount(tempcount);
          setIsdeleteFiltering(false);
        } else {
          Toast.error(
            response?.response?.message || Message.Error.COMMON_MESSAGE
          );
        }
      } catch (error) {
        Toast.error(Message.Error.COMMON_MESSAGE);
      } finally {
        setPageLoading(false);
      }
    },
    [dataPerPage, appliedFilters]
  );

  useEffect(() => {
    if (isFiltering) {
      setIsFiltering(false);
    }
  }, [isFiltering]);

  function handleFilterSubmit(event) {
    event.preventDefault();
    setAppliedFilters(filterValues);
    setIsFilterOpen(false);
    setIsFiltering(true);
  }

  function handleFilterCancel() {
    setIsFilterOpen(false);
  }

  function resetFilter() {
    const defaultFilters = { isGamificationVoucher: 'all' };
    setFilterValues(defaultFilters);
    setAppliedFilters(defaultFilters);
    setIsFiltering(true);
  }

  async function callServiePublishOrUnpublishAPI(data) {
    setPageLoading(true);
    if (data?.id) {
     
      let responseObj = await deleteVoucher(data?.id);
      setPageLoading(false);
    
      if (responseObj?.responseCode === 200) {
        setIsdeleteFiltering(true);
        Toast.success('Voucher Deleted Successfully');
      } else {
        Toast.error(
          responseObj?.response?.message
            ? responseObj?.response?.message
            : Message.Error.COMMON_MESSAGE
        );
      }
    } else {
      Toast.error('Failed to delete');
    }
  }

  function handleDelete(data) {
   
    confirmAlert({
      title: '',
      message: `Are you sure you want to Delete ${data?.voucherTitle} this Voucher?`,
      buttons: [
        {
          label: 'Yes',
          onClick: () => {
            callServiePublishOrUnpublishAPI(data);
          },
        },
        {
          label: 'No',
        },
      ],
      overlayClassName: 'overlay-custom-class-name',
    });
  }
  function handleEdit(data) {
    navigate(`/voucher/edit/:${data.id}`, {
      state: {
        ...data,
      },
    });
  }

  async function handleComplimentaryVoucherSubmit(voucher) {
    setPageLoading(true);
    try {
      if (voucher?.id && values?.mobileNumber && values?.serialNumber) {
        const res = await complimentaryVoucher(
          voucher?.id,
          values?.mobileNumber,
          '3',
          values?.serialNumber
        );
        if (res.responseCode === 200) {
          Toast.success(res?.message);
          onHideModal();
          fetchVoucherData(countcomferomTable);
        } else {
          Toast.error(res?.response?.message || Message.Error.COMMON_MESSAGE);
        }
      } else {
        Toast.error(
          values?.mobileNumber && values?.serialNumber
            ? 'Invalid Voucher Id'
            : 'Invalid Mobile Number or Serial Number'
        );
      }
    } catch (e) {
      Toast.error('Something Went Wrong');
    }
    setPageLoading(false);
  }

  function setChange(e) {
    const { name, value } = e.target;
  
    // Allow only alphanumeric characters if name is "serialNumber"
    const alphanumericRegex = /^[a-zA-Z0-9]*$/;
    
    if (name === "serialNumber" && !alphanumericRegex.test(value)) {
      return; // Ignore the input if it's not alphanumeric
    }
  
    setValues((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  }
  

  const resetValues = () => {
    const currValues = JSON.parse(JSON.stringify(values));
    Object.keys(currValues)?.forEach((key) => {
      currValues[key] = '';
    });
    setValues(currValues);
  };

  const onHideModal = () => {
    setShowModal(false);
    setSelectedVoucher(null);
    resetValues();
    setError('');
  };

  
  function handlePasswordModel(data,key){
    setPasswordmodelOpen(true);
    settempdata(data);
    setkey(key);
  }

  function handlemodelvalues(){
    setPasswordmodelOpen(false);
    setPasswordVal("");

     if(passwordVal && passwordVal!==VOUCHER_PASSWORD.VOUCHER_PASSWORD) 
      {
        Toast.error('Your Entered Password is wrong.' );
        
        return;
      }


    if(key==='edit'){
      handleEdit(tempdata);

      return;

    }
    if(key==='delete'){
      handleDelete(tempdata);

      return;

    }
    if(key==='add'){
      navigate(`${APP_CONSTANTS.CREATE_VOUCHER}`)

      return;

    }


  }

  
  
  const handleComplimentaryAction = (page,row) => {
    setSelectedVoucher(row?.original);
    setError('');
    setShowModal(true);
    setCountcomferomTable(page);

  };

  const handleNumber = (e) => {
    const val = e.target.value.trim();
    setChange(e);
    setError('');

    if (!val) {
      setError('Please enter a mobile number.');
      return;
    }
    if (!/^[6-9]\d{9}$/.test(val)) {
      setError('Mobile number must be exactly 10 digits.');
    }
  };
  

  return {
    handleDelete,
    handleExport,
    isExporting,
    pageLoading,
    dataPerPage,
    totalNumber,
    pageCount,
    navigate,
    handleEdit,
    sectionObj,
    filteredData,
    fetchVoucherData,
    isdeleteFiltering,
    handleComplimentaryVoucherSubmit,
    values,
    setValues,
    setChange,
    resetValues,
    showModal,
    setShowModal,
    error,
    setError,
    onHideModal,
    setSelectedVoucher,
    selectedVoucher,
    filterValues,
    setFilterValues,
    appliedFilters,
    isFilterOpen,
    setIsFilterOpen,
    isFiltering,
    handleFilterSubmit,
    handleFilterCancel,
    resetFilter,
    passwordmodelOpen, setPasswordmodelOpen,
    handlemodelvalues,
    passwordVal, setPasswordVal,
    handlePasswordModel,
    handleComplimentaryAction,
    handleNumber
  };
};

export default useVoucherManagement;
